import { acquireToken } from '@ms/yammer-web-authenticators';

import type { CpsPolicies } from '../store/complianceStore.types';

import type { CpsPolicyApiResponse, CpsPolicyResponse, CpsPolicyResponseValue } from './fetchCpsPolicies.types';
import { CpsPolicySettingMap, cpsPolicyEnabledValue } from './fetchCpsPolicies.types';

/**
 * Extract and normalize policy data from CPS API response.
 */
export const extractPolicies = (responseValues: CpsPolicyResponseValue[] | null): CpsPolicies => {
  const policies: CpsPolicies = {
    SurveyFeedbackFormEnabled: null,
    UserFeedbackFormEnabled: null,
    ConnectedExperiencesEnabled: null,
    AllowScreenshots: null,
    AllowEmailCollection: null,
    AllowLogCollection: null,
  };

  if (!responseValues) {
    return policies;
  }

  for (const responseValue of responseValues) {
    for (const payload of responseValue.policiesPayload) {
      const policyName = CpsPolicySettingMap[payload.settingId];
      if (policyName !== undefined) {
        policies[policyName] = payload.value === cpsPolicyEnabledValue;
      }
    }
  }

  return policies;
};

/**
 * Fetch CPS policies from the Configuration Policy Service.
 *
 * @param host - The CPS host URL (e.g., https://clients.config.office.net)
 * @param requestHash - Optional hash from previous request for bandwidth optimization
 * @returns Processed policy response
 */
export const fetchCpsPolicies = async (host: string, requestHash?: string): Promise<CpsPolicyResponse> => {
  const scopes = [`${host}/.default`];
  const token = await acquireToken({ scopes });

  const url = new URL(`${host}/user/v1.0/web/policies`);
  if (requestHash) {
    url.searchParams.set('PoliciesHash', requestHash);
  }

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'x-Cid': crypto.randomUUID(),
    },
  });

  if (!response.ok) {
    throw new Error(`CPS API request failed: ${String(response.status)} ${response.statusText} (${url.pathname})`);
  }

  const body = (await response.json()) as CpsPolicyApiResponse;
  const checkInTime = response.headers.get('checkin-interval') ?? '60';

  return {
    policiesHash: body.policiesHash,
    policies: extractPolicies(body.value),
    checkInTime,
  };
};
